import { type FC, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  useContentTitlesQuery,
  useContentsQuery,
} from "@/partner/entities/contents";
import {
  mergeContentOptions,
  selectedContentOptions,
  unlabeledContentIds,
  type ContentOption,
} from "../model/contentOptions";

interface ContentsAutocompleteProps {
  label: string;
  value: string[];
  onChange: (contentIds: string[]) => void;
  disabled?: boolean;
  /** Предел числа выбранных: дальше варианты недоступны (проверяет и сервер). */
  max?: number;
}

// Аналог ProductsAutocomplete (см. entities/products/ui/ProductsAutocomplete.tsx):
// варианты из спискового ContentsList (useContentsQuery), а подписи
// выбранных id, которых нет в ответе поиска, одним запросом списка по ids
// (ContentTitles, только id и название), как autocompleteProducts.value.
// Выбранные id всегда остаются опциями (mergeContentOptions): видео вне
// текущей страницы поиска не выпадает из выбора.
export const ContentsAutocomplete: FC<ContentsAutocompleteProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  max,
}) => {
  const [search, setSearch] = useState("");
  // Подписи всех видео, которые уже приходили в ответах.
  const knownLabels = useRef(new Map<string, string>());

  const { data, loading } = useContentsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      filter: { search: search || undefined },
      page: 0,
      rowsPerPage: 20,
    },
  });
  const results: ContentOption[] = (data?.contents?.nodes ?? []).map(
    (node) => ({
      value: node.id,
      label: node.title,
    })
  );
  for (const option of results) {
    knownLabels.current.set(option.value, option.label);
  }

  const unlabeled = unlabeledContentIds(value, knownLabels.current);
  const { data: titles } = useContentTitlesQuery({
    variables: { ids: unlabeled, rowsPerPage: unlabeled.length },
    skip: unlabeled.length === 0,
  });
  for (const node of titles?.contents?.nodes ?? []) {
    knownLabels.current.set(node.id, node.title);
  }

  const resultIds = new Set(results.map((option) => option.value));
  const options = mergeContentOptions(results, value, knownLabels.current);

  return (
    <Autocomplete<ContentOption, true>
      multiple
      fullWidth
      disabled={disabled}
      // Поиск идёт на сервере: в списке только ответ поиска, выбранные
      // видео вне него видны чипами.
      filterOptions={(opts) => opts.filter((o) => resultIds.has(o.value))}
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.label || option.value}
      getOptionDisabled={(option) =>
        max !== undefined &&
        value.length >= max &&
        !value.includes(option.value)
      }
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      value={selectedContentOptions(options, value)}
      onInputChange={(_event, newInputValue, reason) => {
        setSearch(reason === "reset" ? "" : newInputValue);
      }}
      onChange={(_event, newValue) => {
        onChange(newValue.map((option) => option.value));
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};
